import { isAxiosError } from 'axios'
import { feesClient } from '../fees-client'

export interface DeliverySlotsResponse {
  date: string
  fee: {
    type: string
    priceInPence: number
  }
  available: boolean
}

export const AvailabilityErrorCodes = {
  AddressNotFound: 'address-not-found',
  CannotDeliverToAddress: 'cannot-deliver-to-address',
} as const

export class AddressNotFoundError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AddressNotFoundError'
  }
}

export class CannotDeliverToAddressError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CannotDeliverToAddressError'
  }
}

/**
 *
 * @async
 * @param postcode
 * @param vehicleId
 * @param organisationalUnit Pass this when you want delivery slots for a WBAC site
 *
 * @returns {DeliverySlotsResponse[]}
 *
 * @throws {AddressNotFoundError} Address could not be found from postcode
 * @throws {CannotDeliverToAddressError} The business does not support delivering to the postcode area
 */
export const getDeliverySlots = async (
  postcode: string,
  vehicleId: string,
  organisationalUnit?: string,
): Promise<DeliverySlotsResponse[]> => {
  try {
    const { data } = await feesClient.get<DeliverySlotsResponse[]>(
      '/delivery-slots',
      {
        params: {
          postcode,
          vehicleId,
          destinationType: organisationalUnit ? 'wbac' : 'customer',
          organisationalUnit,
        },
      },
    )

    return data
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      switch (error.response.data?.code) {
        case AvailabilityErrorCodes.AddressNotFound: {
          throw new AddressNotFoundError(error.message)
        }
        case AvailabilityErrorCodes.CannotDeliverToAddress: {
          throw new CannotDeliverToAddressError(error.message)
        }
      }
    }

    throw error
  }
}
